import { supabase } from '../providers/supabase/client';
import { supabaseStorageProvider } from '../providers/supabase/storage';

/**
 * Helper to handle missing table / PostgREST schema cache errors clearly
 */
const handleSupabaseError = (error, tableName) => {
  if (!error) return;
  const msg = error.message || error.details || String(error);
  if (
    msg.toLowerCase().includes('could not find the table') ||
    msg.toLowerCase().includes('schema cache') ||
    (msg.toLowerCase().includes('relation') && msg.toLowerCase().includes('does not exist'))
  ) {
    throw new Error(
      `La tabla '${tableName}' no existe en Supabase o su caché no se ha recargado. Por favor ejecuta el script maestro SQL en Supabase SQL Editor.`
    );
  }
  throw error;
};

/**
 * WORKERS CRUD
 */
export const fetchWorkers = async () => {
  const { data, error } = await supabase
    .from('workers')
    .select(`
      *,
      profile:profile_id(id, first_name, last_name, email, avatar_url)
    `)
    .order('last_name', { ascending: true });
  if (error) handleSupabaseError(error, 'public.workers');
  return data;
};

export const createWorker = async (workerData) => {
  const { data, error } = await supabase
    .from('workers')
    .insert([workerData])
    .select(`
      *,
      profile:profile_id(id, first_name, last_name, email, avatar_url)
    `)
    .single();
  if (error) handleSupabaseError(error, 'public.workers');
  return data;
};

export const updateWorker = async (id, workerData) => {
  const { data, error } = await supabase
    .from('workers')
    .update(workerData)
    .eq('id', id)
    .select(`
      *,
      profile:profile_id(id, first_name, last_name, email, avatar_url)
    `)
    .single();
  if (error) handleSupabaseError(error, 'public.workers');
  return data;
};

export const deleteWorker = async (id) => {
  const { error } = await supabase
    .from('workers')
    .delete()
    .eq('id', id);
  if (error) handleSupabaseError(error, 'public.workers');
  return true;
};

/**
 * EXPENSE LOCAL CONCEPTS
 */
export const fetchLocalConcepts = async () => {
  const { data, error } = await supabase
    .from('expense_local_concepts')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });
  if (error) handleSupabaseError(error, 'public.expense_local_concepts');
  return data;
};

export const createLocalConcept = async (conceptData) => {
  const { data, error } = await supabase
    .from('expense_local_concepts')
    .upsert([conceptData], { onConflict: 'name', ignoreDuplicates: true })
    .select();
  if (error) handleSupabaseError(error, 'public.expense_local_concepts');
  return data && data.length > 0 ? data[0] : { name: conceptData.name };
};

export const deleteLocalConcept = async (id) => {
  const { error } = await supabase
    .from('expense_local_concepts')
    .update({ is_active: false })
    .eq('id', id);
  if (error) handleSupabaseError(error, 'public.expense_local_concepts');
  return id;
};

/**
 * EXPENSE RECORDS
 */
export const fetchExpenseRecordsByPeriod = async (period) => {
  const { data, error } = await supabase
    .from('expense_records')
    .select(`
      *,
      worker:worker_id(id, first_name, last_name, dni, position, contract_type, afp_name, base_salary),
      bank_account:bank_account_id(id, name, currency, account_number),
      treasury_tx:treasury_tx_id(*)
    `)
    .eq('period', period);
  if (error) handleSupabaseError(error, 'public.expense_records');
  return data;
};

export const saveExpenseRecord = async (recordData) => {
  const {
    id,
    category,
    subcategory,
    period,
    worker_id,
    amount,
    status,
    bank_account_id,
    payment_date,
    payment_receipt_url,
    support_document_url,
    notes,
    treasury_tx_id,
    exchange_rate = 1.0
  } = recordData;

  const { data: { user } } = await supabase.auth.getUser();

  // Try RPC first for atomic save + treasury sync
  try {
    const { data: rpcResult, error: rpcErr } = await supabase.rpc('save_expense_with_treasury_tx', {
      p_id: id || null,
      p_category: category,
      p_subcategory: subcategory,
      p_period: period,
      p_worker_id: worker_id || null,
      p_amount: parseFloat(amount) || 0,
      p_status: status,
      p_bank_account_id: bank_account_id || null,
      p_payment_date: payment_date || null,
      p_payment_receipt_url: payment_receipt_url || null,
      p_support_document_url: support_document_url || null,
      p_notes: notes || null,
      p_user_id: user?.id || null,
      p_exchange_rate: parseFloat(exchange_rate) || 1.0
    });

    if (!rpcErr && rpcResult && rpcResult.id) {
      // Fetch joined representation for consistent UI state
      const { data: joinedRecord } = await supabase
        .from('expense_records')
        .select(`
          *,
          worker:worker_id(id, first_name, last_name, dni, position, contract_type, afp_name, base_salary),
          bank_account:bank_account_id(id, name, currency, account_number),
          treasury_tx:treasury_tx_id(*)
        `)
        .eq('id', rpcResult.id)
        .single();

      return joinedRecord || rpcResult;
    }
  } catch (err) {
    console.warn('RPC save_expense_with_treasury_tx unavailable, using client fallback:', err);
  }

  // Fallback if RPC is not deployed yet
  let newTreasuryTxId = treasury_tx_id || null;

  if (status === 'PAGADO' && bank_account_id && amount > 0) {
    const txDescription = `[Gasto ${category}] ${subcategory}${worker_id ? ' - Planilla' : ''} (Período ${period})`;
    const txDate = payment_date || new Date().toISOString().split('T')[0];

    const txData = {
      bank_account_id,
      transaction_type: 'OUTBOUND',
      amount: parseFloat(amount),
      description: txDescription,
      transaction_date: txDate,
      exchange_rate: parseFloat(exchange_rate) || 1.0
    };

    if (treasury_tx_id) {
      const { error: txErr } = await supabase
        .from('treasury_transactions')
        .update(txData)
        .eq('id', treasury_tx_id);
      if (txErr) console.warn('Error updating treasury transaction:', txErr);
    } else {
      const { data: txResult, error: txErr } = await supabase
        .from('treasury_transactions')
        .insert([txData])
        .select()
        .single();
      if (!txErr && txResult) {
        newTreasuryTxId = txResult.id;
      }
    }
  } else if (status !== 'PAGADO' && treasury_tx_id) {
    await supabase.from('treasury_transactions').delete().eq('id', treasury_tx_id);
    newTreasuryTxId = null;
  }

  const payload = {
    category,
    subcategory,
    period,
    worker_id: worker_id || null,
    amount: parseFloat(amount) || 0,
    status,
    bank_account_id: bank_account_id || null,
    payment_date: payment_date || null,
    payment_receipt_url: payment_receipt_url || null,
    support_document_url: support_document_url || null,
    treasury_tx_id: newTreasuryTxId,
    notes: notes || null
  };

  let recordId = id;
  if (id) {
    const { data, error } = await supabase
      .from('expense_records')
      .update(payload)
      .eq('id', id)
      .select()
      .single();
    if (error) handleSupabaseError(error, 'public.expense_records');
    recordId = data.id;
  } else {
    const { data, error } = await supabase
      .from('expense_records')
      .insert([payload])
      .select()
      .single();
    if (error) handleSupabaseError(error, 'public.expense_records');
    recordId = data.id;
  }

  // Fetch full joined shape
  const { data: joinedRecord } = await supabase
    .from('expense_records')
    .select(`
      *,
      worker:worker_id(id, first_name, last_name, dni, position, contract_type, afp_name, base_salary),
      bank_account:bank_account_id(id, name, currency, account_number),
      treasury_tx:treasury_tx_id(*)
    `)
    .eq('id', recordId)
    .single();

  return joinedRecord;
};

export const deleteExpenseRecord = async (id, treasuryTxId = null) => {
  if (treasuryTxId) {
    await supabase.from('treasury_transactions').delete().eq('id', treasuryTxId);
  }
  const { error } = await supabase
    .from('expense_records')
    .delete()
    .eq('id', id);
  if (error) handleSupabaseError(error, 'public.expense_records');
  return true;
};

/**
 * UPLOAD FILE HELPERS
 */
export const uploadExpenseDocument = async (file, pathFolder) => {
  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 7)}.${fileExt}`;
  const filePath = `${pathFolder}/${fileName}`;

  // List of candidate buckets in order of preference
  const candidateBuckets = ['expense-documents', 'payment-receipts', 'passenger_docs'];
  let lastError = null;

  for (const bucket of candidateBuckets) {
    try {
      const uploadPath = bucket === 'expense-documents' ? filePath : `expenses/${filePath}`;
      const { data, error } = await supabaseStorageProvider.uploadFile(bucket, uploadPath, file);

      if (!error && data) {
        return supabaseStorageProvider.getPublicUrl(bucket, uploadPath);
      }

      lastError = error;
      console.warn(`Upload attempt for bucket '${bucket}' failed:`, error?.message || error);
    } catch (err) {
      lastError = err;
      console.warn(`Exception during upload to bucket '${bucket}':`, err);
    }
  }

  const errorMsg =
    lastError?.message ||
    lastError?.error ||
    'Bucket no encontrado. Crea el bucket public "expense-documents" en Supabase Storage.';

  throw new Error(`Error de almacenamiento: ${errorMsg}`);
};
